import React, { useState, useEffect } from 'react';
import './ChemicalDisclosure.css';
import ChemicalDisclosureTable from './ChemicalDisclosureTable';
import IngredientsService from '../services/IngredientsService';
import { toast } from 'react-toastify';

const MultiWellChemicalDisclosure = ({ selectedWells, waterSource, wellIngredients, onIngredientsChange, onComplete }) => {
  const [activeWellIndex, setActiveWellIndex] = useState(0);
  const [wellValidation, setWellValidation] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedWells, setProcessedWells] = useState({});


  // Process chemicals for all wells on component mount
  useEffect(() => {
    const loadIngredientsForAllWells = async () => {
      if (!selectedWells || selectedWells.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        const newProcessedWells = {};
        
        for (const well of selectedWells) {
          const wellName = well.wellName || well.name;
          
          // Use the IngredientsService.findCustomerIdByName instead of local function
          // to avoid duplicate API calls and use caching
          let customerId = null;
          
          if (well.customer) {
            console.log(`Looking up customer ID for well ${wellName}: ${well.customer}`);
            customerId = await IngredientsService.findCustomerIdByName(well.customer);
            console.log(`Found customer ID from lookup: ${customerId}`);
          }
          
          // If lookup failed, fall back to the existing customer_id
          if (!customerId) {
            customerId = well.customer_id;
            console.log(`Using existing customer_id: ${customerId}`);
          }
          
          // If no customer ID found, log a warning but don't set a hardcoded value
          if (!customerId) {
            console.log(`No customer ID found from lookup for well ${wellName}. Proceeding without a customer ID.`);
          }
          
          // Ensure customer_id is set in wellData for third-party chemical mappings
          const wellDataWithCustomerId = {
            ...well,
            customer_id: customerId
          };
          
          console.log(`Processing chemicals for well ${wellName}:`, wellDataWithCustomerId);
          
          // Process chemicals from wellData
          const processedIngredients = await IngredientsService.processChemicals(wellDataWithCustomerId);
          
          newProcessedWells[wellName] = {
            wellData: wellDataWithCustomerId,
            ingredients: processedIngredients
          };
          
          // Update parent component with ingredients for this well
          if (onIngredientsChange) {
            onIngredientsChange(wellName, processedIngredients);
          }
        }
        
        setProcessedWells(newProcessedWells);
        
      } catch (error) {
        console.error('Error processing chemicals for wells:', error);
        setError('Failed to process chemicals. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadIngredientsForAllWells();
  }, [selectedWells, onIngredientsChange]);

  // Validate ingredients for a specific well
  const validateIngredients = (ingredients) => {
    if (!ingredients || ingredients.length === 0) {
      return false;
    }
    
    // Calculate total percentage
    const totalPercentage = ingredients.reduce((sum, ingredient) => {
      return sum + (ingredient.percentInHFJob || 0);
    }, 0);
    
    // Check if total is within acceptable range (0.97 - 1.03)
    const isPercentageValid = totalPercentage >= 0.97 && totalPercentage <= 1.03;
    
    // Check if all required fields are filled
    const hasRequiredFields = ingredients.every(ingredient => 
      ingredient.ingredientName && 
      (ingredient.isNonMsds || 
        (ingredient.tradeName && ingredient.supplier && ingredient.purpose))
    );
    
    // Check if all ingredients have a valid mass
    const hasValidMass = ingredients.every(ingredient => 
      parseFloat(ingredient.massIngredient) > 0
    );
    
    // Check if all proprietary chemicals have vendor associations
    const hasVendorAssociations = ingredients.every(ingredient => 
      !(ingredient.casNumber && 
        (ingredient.casNumber.toLowerCase().includes('proprietary') || 
         ingredient.casNumber.toLowerCase() === 'n/a')) || 
      ingredient.supplier
    );
    
    return isPercentageValid && hasRequiredFields && hasValidMass && 
           hasVendorAssociations && ingredients.length > 0;
  };

  // Update validation when ingredients change
  useEffect(() => {
    const newValidation = {};
    Object.keys(processedWells).forEach(wellName => {
      const ingredients = wellIngredients[wellName] || processedWells[wellName]?.ingredients || [];
      newValidation[wellName] = validateIngredients(ingredients);
    });
    setWellValidation(newValidation);
  }, [wellIngredients, processedWells]);

  // Handle ingredients update for a specific well
  const handleIngredientsUpdate = (wellName, updatedIngredients) => {
    if (onIngredientsChange) {
      onIngredientsChange(wellName, updatedIngredients);
    }
  };

  // Check if all wells are valid
  const areAllWellsValid = () => {
    return Object.values(wellValidation).every(isValid => isValid);
  };

  // Handle completion - proceed to XML generation
  const handleComplete = () => {
    if (!areAllWellsValid()) {
      toast.error('Please correct all errors in all wells before proceeding');
      return;
    }
    
    try {
      // Prepare data for XML generation
      const updatedWells = selectedWells.map(well => {
        const wellName = well.wellName || well.name;
        const ingredients = wellIngredients[wellName] || processedWells[wellName]?.ingredients || [];
        
        // Calculate total mass for wellData
        const totalMass = ingredients.reduce((sum, ingredient) => {
          return sum + (parseFloat(ingredient.massIngredient) || 0);
        }, 0);
        
        // Get the customer_id from the processed well data
        const processedWellData = processedWells[wellName]?.wellData;
        const customerId = processedWellData?.customer_id;
        
        return {
          ...well,
          customer_id: customerId,
          totalMass: totalMass,
          waterSource: waterSource,
          // Ensure all required fields are present
          wellName: well.wellName || well.name,
          apiNumber: well.apiNumber || well.api,
          startDate: well.startDate || well.startTime,
          endDate: well.endDate || well.endTime,
          state: well.state,
          county: well.county,
          tvd: well.tvd,
          totalClean: well.totalCleanVolume || well.clean,
          longitude: well.longitude,
          latitude: well.latitude,
          operatorNumber: well.operatorNumber || well.operator,
          federalWell: well.federalWell || 'No',
          indianWell: well.indianWell || 'No',
          datum: well.datum || 'NAD83'
        };
      });
      
      // Call onComplete callback with updated data
      if (typeof onComplete === 'function') {
        onComplete(updatedWells, wellIngredients);
      }
    } catch (error) {
      console.error('Error preparing data for XML generation:', error);
      toast.error(`Failed to prepare data: ${error.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="chemical-disclosure">
        <div className="loading-message">Loading ingredients data for all wells...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chemical-disclosure">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const activeWell = selectedWells[activeWellIndex];
  const activeWellName = activeWell?.wellName || activeWell?.name;
  const activeIngredients = wellIngredients[activeWellName] || processedWells[activeWellName]?.ingredients || [];
  const isActiveWellValid = wellValidation[activeWellName] || false;

  return (
    <div className="chemical-disclosure">
      <div className="chemical-disclosure-header">
        <h2>Chemical Disclosure - Multiple Wells</h2>
        <p>Review and edit chemical ingredients for each well's FracFocus disclosure.</p>
        
        {waterSource && (
          <div className="water-source-info">
            <p>
              <strong>Water Source (shared):</strong>{' '}
              {typeof waterSource === 'object' 
                ? (waterSource.type || waterSource.source || JSON.stringify(waterSource).replace(/[{}"]/g, ''))
                : waterSource}
            </p>
          </div>
        )}
      </div>

      {/* Well Tabs */}
      <div className="well-tabs">
        {selectedWells.map((well, index) => {
          const wellName = well.wellName || well.name;
          const isValid = wellValidation[wellName];
          return (
            <button
              key={wellName}
              className={`well-tab ${index === activeWellIndex ? 'active' : ''} ${isValid ? 'valid' : 'invalid'}`}
              onClick={() => setActiveWellIndex(index)}
            >
              {wellName}
              {isValid ? ' ✓' : ' ⚠'}
            </button>
          );
        })}
      </div>

      {/* Active Well Content */}
      {activeWell && (
        <div className="active-well-content">
          <div className="well-summary">
            <h3>{activeWellName}</h3>
            <div className="well-details">
              <p><strong>API:</strong> {activeWell.apiNumber || activeWell.api || 'N/A'}</p>
              <p><strong>TVD:</strong> {activeWell.tvd ? Math.round(activeWell.tvd) : 'N/A'} {activeWell.tvd ? 'ft' : ''}</p>
              <p><strong>Clean Volume:</strong> {activeWell.totalCleanVolume || activeWell.clean ? Math.round(activeWell.totalCleanVolume || activeWell.clean) : 'N/A'} {activeWell.totalCleanVolume || activeWell.clean ? 'gal' : ''}</p>
            </div>
          </div>

          <ChemicalDisclosureTable 
            ingredients={activeIngredients}
            setIngredients={(updatedIngredients) => handleIngredientsUpdate(activeWellName, updatedIngredients)}
            wellData={activeWell}
            totalClean={activeWell?.totalCleanVolume || activeWell?.clean || 0}
          />

          {!isActiveWellValid && (
            <div className="validation-warning">
              <p>Please correct the following for {activeWellName} before proceeding:</p>
              <ul>
                <li>Ensure all ingredients have a name</li>
                <li>MSDS components must have trade name, supplier, and purpose</li>
                <li>Total percentage should be between 97% and 103%</li>
                <li>All ingredients must have a valid mass (volume × 8.345 × specific gravity)</li>
                <li>All CAS numbers should be valid or marked as Proprietary/N/A</li>
                <li>All proprietary chemicals must have a supplier/vendor association</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Overall Status and Actions */}
      <div className="multi-well-status">
        <div className="wells-status-summary">
          <h4>Wells Status:</h4>
          <ul>
            {selectedWells.map(well => {
              const wellName = well.wellName || well.name;
              const isValid = wellValidation[wellName];
              return (
                <li key={wellName} className={isValid ? 'valid' : 'invalid'}>
                  {wellName}: {isValid ? 'Ready' : 'Needs attention'}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="disclosure-actions">
          <button 
            className="complete-button"
            onClick={handleComplete}
            disabled={!areAllWellsValid()}
          >
            Proceed to XML Generation ({Object.values(wellValidation).filter(Boolean).length}/{selectedWells.length} wells ready)
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiWellChemicalDisclosure;
